import React, { useState, useEffect } from "react";
import { notification } from "antd";
import { useDispatch, useSelector } from 'react-redux';
import { Mail, CheckCircle, RotateCcw } from "lucide-react";
import { useLocation, useNavigate } from 'react-router-dom';

import { TokenVerification, SendVerificationEmail } from "../../../slices/auth"
import { storeToken } from "../../profile/component/utils";
import { toast } from "../../../hooks/use-toast";

const EmailVerificationStep = ({ onNext, goToStep, data }) => {
  const [isVerified, setIsVerified] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { user, token: jwtToken } = useSelector(state => state?.auth || {});
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');
  const email = queryParams.get('email');

  const dispatch = useDispatch();

  const userEmail = data.userInfo?.email || email || user.email ||  "your-email@example.com";

  const handleResendEmail =  () => {
    setIsResending(true);
    dispatch(SendVerificationEmail({ user: { email: userEmail }, type: 'sign-in'})).then(() => {
      setIsResending(false);

      toast({
        title: 'Email Sent',
        description: "We've sent another verification email to your inbox.",
      })
    })
  };


  useEffect(() => {
    if(token) {
      const verifyEmail = async () => {
        const response = await dispatch(TokenVerification({ token, type: 'sign-in', email: userEmail }))
        if (!response.error) {
          const { jwt } = response?.payload?.data || {};
          setIsVerified(true);
          // Store verification data for other tabs
          const verificationData = {
            timestamp: Date.now().toString(),
            token: token,
            email: userEmail,
            verified: true
          };
          localStorage.setItem("emailVerified", JSON.stringify(verificationData));
          setTimeout(() => {
            setIsVerified(true);
            storeToken(jwt, navigate, dispatch);
            navigate(location.pathname, { replace: true });
            onNext({ emailVerified: true });
          }, 1000);
        }
        else{
          console.log('Token verification failed:', response);
          const { alreadyVerified, sw_payment_flag, sw_store_flag, store_sync_started_at } = response.payload?.err?.data
          const startedAt = new Date(store_sync_started_at).getTime();
          const thirtyMinutesAgo = Date.now() - 30 * 60 * 1000;
          if (alreadyVerified) {
            // Route user to the next pending step using onboarding state manager
            if (jwtToken){
              if (!sw_payment_flag) {
                goToStep && goToStep(3);
              } else if (!sw_store_flag) {
                goToStep && goToStep(4);
              } else if (startedAt > thirtyMinutesAgo) {
                goToStep && goToStep(5);
              }
              return;
            }
            else{
              navigate('/auth/sign-in', { replace: true });
            }

          }
        }
      }
      verifyEmail();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const handleStorageEvent = (e) => {
      if (e.key === "emailVerified" && e.newValue) {
        try {
          const verificationData = JSON.parse(e.newValue);
          if (verificationData.verified) {
            localStorage.removeItem("emailVerified");

            if (window.opener) {
              window.open('', '_self', ''); window.close();
            } else {
              // Sync step with other tabs using stored verification data
              setIsVerified(true);
              setTimeout(() => {
                onNext({ emailVerified: true });
              }, 1000);
            }
          }
        } catch (error) {
          console.error("Error parsing verification data:", error);
        }
      }
    };

    window.addEventListener("storage", handleStorageEvent);

    return () => {
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [onNext]);

  return (
    <div className="email-verification-card">
      <div className="email-verification-body animate-slide-in-right">
            <div style={{ marginBottom: '28px' }}>
              {isVerified ? (
                <CheckCircle className="w-16 h-16 text-success mx-auto animate-pulse-success" />
              ) : (
                <Mail className="w-16 h-16 text-primary mx-auto" />
              )}
            </div>

        <h2 className="card-title">
          {isVerified ? "Email Verified!" : "Check Your Email"}
        </h2>
        
        {!isVerified ? (
          <>
            <p className="description email-verification-description">
              We've sent a verification email to{" "}
              <span style={{ fontWeight: '600' }} className="text-primary">{userEmail}</span>
              <br />
              Please check your inbox and click the verification link to continue.
            </p>

            <div className="info-box">
              <p className="info-text">
                <strong>Didn't receive the email?</strong> Check your spam folder or try resending it.
              </p>
            </div>

                <div className="actions-column">
                  <button
                    onClick={handleResendEmail}
                    disabled={isResending}
                    className="btn-outline"
                  >
                   {isResending ? <RotateCcw className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                    {isResending ? "Resending..." : "Resend Verification Email"}
                  </button>

                  {/* <button
                    onClick={handleVerified}
                    className="btn-primary"
                  >
                    I've Verified, Continue
                  </button> */}
                </div>
          </>
        ) : (
          <div>
            <p className="description">
              Great! Your email has been verified successfully.
              <br />
              Redirecting you to the next step...
            </p>
            <div className="loading-bar"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export { EmailVerificationStep };
